import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UserProfile } from '../../../../types/types';
import { AppSyncUser } from '../../../../services/auth/types/user';
import { getTripById, Trip, createTrip, updateTrip, deleteTrip, normalizeStatus, getStatusDisplay, formatBudget, formatDestinations, getActivities, createActivity, updateActivity, buildTripId, extractActivityUuid, extractTripUuid } from '../../../../services/trips';
import type { TripDates, CreateTripRequest, Activity, CreateActivityRequest } from '../../../../services/trips/api/tripsApi';
import { formatTripDates, formatDateDisplay, calculateDaysRemaining } from '../../../../services/trips/utils/dateUtils';
import { DestinationInput } from './helpers';
import { ProgressBar, DaysRemaining } from '../Trips/helpers';
import './TripDetail.css';
import '../Plan/Plan.css';

// Type declarations for Google Maps
declare global {
  interface Window {
    google: any;
  }
}

interface TripDetailProps {
  user: UserProfile;
  appSyncUser: AppSyncUser | null;
}

const TripDetail: React.FC<TripDetailProps> = ({ user, appSyncUser }) => {
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<any>(null);
  const markersRef = useRef<any[]>([]);
  const geocodedLocationRef = useRef<{lat: number; lng: number; address?: string} | null>(null);
  const [trip, setTrip] = useState<Trip | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<{lat: number; lng: number} | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<{
    title: string;
    destinations: string[];
    dates: TripDates | null;
    budget: string;
    budgetCurrency: string;
    description: string;
    location: { lat: number; lng: number; address: string } | null;
  }>({
    title: '',
    destinations: [],
    dates: null,
    budget: '',
    budgetCurrency: 'USD',
    description: '',
    location: null
  });
  
  // Validation errors
  const [validationErrors, setValidationErrors] = useState<{
    title?: string;
    dates?: string;
    destinations?: string;
    budget?: string;
  }>({});

  // Activities state
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);
  const [activityFormData, setActivityFormData] = useState<{
    title: string;
    location: string;
    dateTime: string;
    budget: string;
    budgetCurrency: string;
  }>({
    title: '',
    location: '',
    dateTime: '',
    budget: '',
    budgetCurrency: 'USD'
  });

  // Chat message state for displaying conversation
  interface AgentResponse {
    intent: string;
    confidence: number;
    message: string;
    trip_draft?: any;
    locations_recommendations?: any[];
  }
  
  interface ChatMessage {
    id: string;
    message: string;
    isUser: boolean;
    timestamp: Date;
    intent?: string;
    trip_draft?: any;
    isHistorical?: boolean;  // Flag to indicate this is from history, not live chat
  }
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const chatMessagesEndRef = useRef<HTMLDivElement | null>(null);

  // Parse agent response from JSON text (same as Plan page)
  const parseAgentResponse = useCallback((text: string): { response: AgentResponse | null; isError: boolean } => {
    if (!text || typeof text !== 'string') {
      return { response: null, isError: false };
    }

    const trimmed = text.trim();
    
    // Check if it's a JSON response
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed) as any;
        
        // Check if response is nested in a "response" object
        let agentResponse: AgentResponse | null = null;
        if (parsed.response && parsed.response.intent) {
          // Nested format: { "response": { "intent": "...", ... } }
          agentResponse = parsed.response as AgentResponse;
        } else if (parsed.intent) {
          // Direct format: { "intent": "...", ... }
          agentResponse = parsed as AgentResponse;
        }
        
        // Validate required fields
        if (agentResponse && agentResponse.intent && agentResponse.message !== undefined && agentResponse.confidence !== undefined) {
          return { response: agentResponse, isError: false };
        } else {
          console.warn('⚠️ Invalid agent response format - missing required fields:', parsed);
          return { response: null, isError: false };
        }
      } catch (error) {
        // Not valid JSON, treat as plain text message
        console.warn('⚠️ Failed to parse JSON response:', error);
        return { response: null, isError: false };
      }
    }

    // Not JSON, treat as plain text
    return { response: null, isError: false };
  }, []);

  const handleBack = () => {
    // Navigate back to trips page
    navigate('/trips');
  };

  const handleEdit = () => {
    setIsEditMode(true);
    // Clear map and markers so it re-initializes with the correct location for edit mode
    markersRef.current.forEach((marker) => {
      marker.map = null;
    });
    markersRef.current = [];
    setMap(null);
  };

  const handleCancel = () => {
    if (isNewTrip) {
      navigate('/trips');
    } else {
      setIsEditMode(false);
      // Clear map and markers so it re-initializes with the correct location for view mode
      markersRef.current.forEach((marker) => {
        marker.map = null;
      });
      markersRef.current = [];
      setMap(null);
      // Reset form data to trip data
      if (trip) {
        setFormData({
          title: trip.title || '',
          destinations: trip.destinations || [],
          dates: trip.dates || null,
          budget: typeof trip.budget === 'object' && trip.budget !== null 
            ? trip.budget.amount.toString() 
            : typeof trip.budget === 'number' 
              ? trip.budget.toString() 
              : (trip.budget || ''),
          budgetCurrency: typeof trip.budget === 'object' && trip.budget !== null 
            ? trip.budget.currency 
            : (trip as any).budgetCurrency || 'USD',
          description: trip.description || '',
          location: trip.location || null
        });
      }
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear validation errors
    setValidationErrors({});
    setSaveError(null);
  };

  const handleDestinationChange = (index: number, value: string) => {
    const newDestinations = [...formData.destinations];
    newDestinations[index] = value;
    handleInputChange('destinations', newDestinations);
  };

  const handleAddDestination = () => {
    handleInputChange('destinations', [...formData.destinations, '']);
  };

  const handleRemoveDestination = (index: number) => {
    const newDestinations = formData.destinations.filter((_, i) => i !== index);
    handleInputChange('destinations', newDestinations);
  };

  const validateForm = (): boolean => {
    const errors: { title?: string; dates?: string; destinations?: string; budget?: string } = {};
    
    if (!formData.title || !formData.title.trim()) {
      errors.title = 'Trip title is required';
    }
    
    if (!formData.dates || !formData.dates.startDate || !formData.dates.endDate) {
      errors.dates = 'Trip dates are required';
    } else {
      // Validate that start date is before or equal to end date
      const startDate = new Date(formData.dates.startDate);
      const endDate = new Date(formData.dates.endDate);
      if (startDate > endDate) {
        errors.dates = 'Start date must be before or equal to end date';
      }
    }
    
    if (!formData.destinations || formData.destinations.length === 0 || !formData.destinations.some(d => d.trim())) {
      errors.destinations = 'At least one destination is required';
    }
    
    // Validate budget: must be >= 0, only numbers and one dot allowed
    if (formData.budget && formData.budget.trim()) {
      const budgetValue = parseFloat(formData.budget);
      if (isNaN(budgetValue) || budgetValue < 0) {
        errors.budget = 'Budget must be a number greater than or equal to 0';
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDelete = async () => {
    if (!tripId || isNewTrip) {
      return;
    }

    const confirmDelete = window.confirm(
      'Are you sure you want to delete this trip? This action cannot be undone.'
    );

    if (!confirmDelete) {
      return;
    }

    setSaveError(null);
    setIsSaving(true);

    try {
      await deleteTrip(tripId);
      console.log('✅ Trip deleted successfully');
      // Navigate back to trips list
      navigate('/trips');
    } catch (err: any) {
      console.error('Failed to delete trip:', err);
      setSaveError(err.message || 'Failed to delete trip. Please try again.');
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    setSaveError(null);
    
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      // Parse budget from string to object format
      let budgetObj: { amount: number; currency: string } | undefined = undefined;
      if (formData.budget && formData.budget.trim()) {
        const budgetValue = parseFloat(formData.budget);
        if (!isNaN(budgetValue) && budgetValue > 0) {
          budgetObj = {
            amount: budgetValue,
            currency: formData.budgetCurrency || 'USD'
          };
        }
      }
      
      const tripData: CreateTripRequest = {
        title: formData.title || 'Untitled Trip',
        destinations: formData.destinations.filter(d => d.trim()),
        dates: formData.dates!,
        status: 'planning',
        budget: budgetObj,
        description: formData.description || undefined
      };

      if (isNewTrip) {
        // Create new trip
        const createdTrip = await createTrip(tripData);
        // Extract UUID from the full trip ID (TRIP#uuid) for navigation
        const tripUuid = extractTripUuid(createdTrip.id);
        console.log('✅ Trip created:', { fullId: createdTrip.id, uuid: tripUuid });
        navigate(`/trips/${tripUuid}`);
      } else if (trip) {
        // Update existing trip
        // Extract UUID from trip.id (TRIP#uuid) for API call
        const tripUuid = extractTripUuid(trip.id);
        console.log('📝 Updating trip:', { originalId: trip.id, uuid: tripUuid });
        const updatedTrip = await updateTrip({
          id: tripUuid, // Use UUID - backend will construct TRIP#uuid internally
          ...tripData
        });
        setTrip(updatedTrip as Trip);
        setIsEditMode(false);
      }
    } catch (err: any) {
      console.error('Failed to save trip:', err);
      setSaveError(err.message || 'Failed to save trip. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Activity handlers
  const handleCreateActivity = () => {
    setEditingActivity(null);
    setActivityFormData({
      title: '',
      location: trip?.destinations?.[0] || '',
      dateTime: '',
      budget: '',
      budgetCurrency: 'USD'
    });
    setShowActivityForm(true);
  };

  const handleEditActivity = (activity: Activity) => {
    setEditingActivity(activity);
    setActivityFormData({
      title: activity.title,
      location: activity.location,
      dateTime: activity.dateTime || '',
      budget: activity.budget ? activity.budget.amount.toString() : '',
      budgetCurrency: activity.budget?.currency || 'USD'
    });
    setShowActivityForm(true);
  };

  const handleCancelActivityForm = () => {
    setShowActivityForm(false);
    setEditingActivity(null);
    setActivityFormData({
      title: '',
      location: '',
      dateTime: '',
      budget: '',
      budgetCurrency: 'USD'
    });
  };

  const handleSaveActivity = async () => {
    console.log('🔍 handleSaveActivity called with tripId:', tripId, 'isNewTrip:', isNewTrip);
    
    if (!tripId || isNewTrip || !activityFormData.title || !activityFormData.location) {
      console.warn('⚠️ Activity save blocked:', { tripId, isNewTrip, hasTitle: !!activityFormData.title, hasLocation: !!activityFormData.location });
      return;
    }

    try {
      // Parse budget if provided
      let budgetObj: { amount: number; currency: string } | undefined = undefined;
      if (activityFormData.budget && activityFormData.budget.trim()) {
        const budgetValue = parseFloat(activityFormData.budget);
        if (!isNaN(budgetValue) && budgetValue > 0) {
          budgetObj = {
            amount: budgetValue,
            currency: activityFormData.budgetCurrency || 'USD'
          };
        }
      }

      const activityData: CreateActivityRequest = {
        title: activityFormData.title,
        location: activityFormData.location,
        dateTime: activityFormData.dateTime || undefined,
        budget: budgetObj
      };

      if (editingActivity) {
        // Update existing activity
        // Extract activity UUID from the composite key (TRIP#...#ACTIVITY#uuid)
        const activityUuid = extractActivityUuid(editingActivity.id);
        console.log('📝 Updating activity:', { 
          tripId, 
          originalActivityId: editingActivity.id, 
          activityUuid 
        });
        // Use UUIDs directly - backend will construct composite keys internally
        const updatedActivity = await updateActivity(tripId, activityUuid, activityData);
        setActivities(activities.map(a => a.id === updatedActivity.id ? updatedActivity : a));
        console.log('✅ Activity updated:', updatedActivity);
      } else {
        // Create new activity
        console.log('➕ Creating activity:', { tripId, activityData });
        // Use UUID directly - backend will construct TRIP#uuid internally
        const createdActivity = await createActivity(tripId, activityData);
        setActivities([...activities, createdActivity]);
        console.log('✅ Activity created:', createdActivity);
      }

      handleCancelActivityForm();
    } catch (err: any) {
      console.error('❌ Failed to save activity:', err);
      alert(err.message || 'Failed to save activity. Please try again.');
    }
  };

  // Fetch conversation messages for the trip
  const fetchConversation = useCallback(async (conversationId: string) => {
    const conversationsEndpoint = process.env.REACT_APP_APIGATEWAY_CONVERSATIONS_ENDPOINT;
    
    if (!conversationsEndpoint) {
      console.warn('⚠️ REACT_APP_APIGATEWAY_CONVERSATIONS_ENDPOINT not configured');
      return;
    }

    setIsLoadingConversation(true);
    try {
      console.log('📡 Fetching conversation for trip:', conversationId);
      
      // Get ID token from cache for Cognito authentication
      const { getTokensFromCache } = await import('../../../../services/auth/utils/tokenCache');
      const tokens = getTokensFromCache();
      
      if (!tokens?.idToken) {
        throw new Error('User not authenticated - no ID token found');
      }
      
      // Build endpoint: /conversation?conversation_id=abc123
      let endpoint = conversationsEndpoint;
      if (endpoint.endsWith('/conversations')) {
        endpoint = endpoint.replace(/\/conversations$/, '/conversation');
      } else {
        endpoint = endpoint.replace(/\/$/, '') + '/conversation';
      }
      endpoint += `?conversation_id=${encodeURIComponent(conversationId)}`;
      
      console.log('🌐 Fetching conversation from:', endpoint);
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.idToken}` // Using Bearer + ID token
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch conversation: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('✅ Conversation fetched:', data);
      
      // Format the messages to match ChatMessage interface
      // API returns: { conversation_id, messages: [{message_id, created_at, text, actor}], count }
      const messages = data.messages || [];
      const formattedMessages: ChatMessage[] = messages.map((msg: any) => {
        const baseMessage: ChatMessage = {
          id: msg.message_id?.toString() || `msg-${Date.now()}`,
          message: msg.text || '',
          isUser: msg.actor === 'user',
          timestamp: new Date(msg.created_at * 1000), // Convert Unix timestamp to Date
          isHistorical: true  // Mark as historical so we don't show interactive elements
        };
        
        // Parse assistant messages to extract structured data (same as Plan page)
        if (msg.actor === 'assistant' && msg.text) {
          const { response: agentResponse } = parseAgentResponse(msg.text);
          if (agentResponse) {
            baseMessage.message = agentResponse.message;
            baseMessage.intent = agentResponse.intent;
            baseMessage.trip_draft = agentResponse.trip_draft;
          }
        }
        
        return baseMessage;
      });
      
      setChatMessages(formattedMessages);
      console.log('✅ Chat messages loaded:', formattedMessages.length);
      
      // Scroll to bottom after loading
      setTimeout(() => {
        chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      
    } catch (error: any) {
      console.error('❌ Failed to fetch conversation:', error);
      setChatMessages([{
        id: `error-${Date.now()}`,
        message: `Could not load conversation history: ${error.message}`,
        isUser: false,
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoadingConversation(false);
    }
  }, [parseAgentResponse]);
  
  // Get Google Maps API key from environment variables
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const hasApiKey = !!googleMapsApiKey;

  // Check if this is a new trip
  const isNewTrip = tripId === 'new';

  // Initialize form data for new trip
  useEffect(() => {
    if (isNewTrip) {
      setIsEditMode(true);
      setIsLoading(false);
      // Clear geocoded location cache for new trip
      geocodedLocationRef.current = null;
      setFormData({
        title: '',
        destinations: [],
        dates: null,
        budget: '',
        budgetCurrency: 'USD',
        description: '',
        location: null
      });
    }
  }, [isNewTrip]);

  // Get user location (for new trips and edit mode)
  useEffect(() => {
    if (isNewTrip || isEditMode) {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setUserLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
            console.log('✅ User location obtained:', {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
          },
          (error) => {
            console.warn('⚠️ Error getting user location:', error.message);
          }
        );
      } else {
        console.warn('⚠️ Geolocation is not supported by this browser');
      }
    }
  }, [isNewTrip, isEditMode]);

  // Fetch trip data from API
  useEffect(() => {
    const fetchTrip = async () => {
      if (!tripId || isNewTrip) {
        return;
      }

      // Clear geocoded location cache when loading a different trip
      geocodedLocationRef.current = null;

      try {
        setIsLoading(true);
        setError(null);
        console.log('📥 Fetching trip with tripId (UUID) from URL params:', tripId);
        // Use UUID directly - backend will construct TRIP#uuid internally
        const fetchedTrip = await getTripById(tripId);
        console.log('✅ Trip fetched:', { id: fetchedTrip.id, title: fetchedTrip.title, conversation_id: fetchedTrip.conversation_id });
        setTrip(fetchedTrip);
        
        // Load conversation if trip has one
        if (fetchedTrip.conversation_id) {
          console.log('📖 Trip has conversation_id, loading conversation:', fetchedTrip.conversation_id);
          fetchConversation(fetchedTrip.conversation_id);
        }
        
        // Initialize form data with trip data
        setFormData({
          title: fetchedTrip.title || '',
          destinations: fetchedTrip.destinations || [],
          dates: fetchedTrip.dates || null,
          budget: typeof fetchedTrip.budget === 'object' && fetchedTrip.budget !== null 
            ? fetchedTrip.budget.amount.toString() 
            : typeof fetchedTrip.budget === 'number' 
              ? fetchedTrip.budget.toString() 
              : (fetchedTrip.budget || ''),
          budgetCurrency: typeof fetchedTrip.budget === 'object' && fetchedTrip.budget !== null 
            ? fetchedTrip.budget.currency 
            : (fetchedTrip as any).budgetCurrency || 'USD',
          description: fetchedTrip.description || '',
          location: fetchedTrip.location || null
        });
      } catch (err: any) {
        console.error('Failed to fetch trip:', err);
        setError(err.message || 'Failed to load trip. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrip();
  }, [tripId, isNewTrip]);

  // Fetch activities when trip is loaded
  useEffect(() => {
    const fetchTripActivities = async () => {
      if (!tripId || isNewTrip) {
        return;
      }

      try {
        setIsLoadingActivities(true);
        console.log('📥 Fetching activities for tripId (UUID):', tripId);
        // Use UUID directly - backend will construct TRIP#uuid internally
        const fetchedActivities = await getActivities(tripId);
        setActivities(fetchedActivities);
        console.log('✅ Activities loaded:', fetchedActivities);
      } catch (err: any) {
        console.error('Failed to fetch activities:', err);
        // Don't block the page if activities fail to load
      } finally {
        setIsLoadingActivities(false);
      }
    };

    fetchTripActivities();
  }, [tripId, isNewTrip]);

  // Initialize Google Maps (for view mode or edit mode with location)
  useEffect(() => {
    if (!hasApiKey) {
      console.log('⚠️ Map initialization skipped: no API key');
      return;
    }

    if (!mapRef.current) {
      console.log('⚠️ Map initialization skipped: mapRef.current is null');
      return;
    }

    // Don't initialize if still loading trip data (unless it's a new trip or edit mode)
    if (!isNewTrip && !isEditMode && isLoading) {
      console.log('⚠️ Map initialization skipped: trip data still loading');
      return;
    }

    let checkInterval: NodeJS.Timeout | null = null;

    const initializeMap = () => {
      // Determine location to use: geocode destination name for better regional view
      let locationToUse: {lat: number; lng: number; address?: string} | null = null;
      
      // First check if we have a geocoded location from previous attempt
      if (geocodedLocationRef.current) {
        locationToUse = geocodedLocationRef.current;
      } else if (isNewTrip || isEditMode) {
        // For new trips or edit mode, use first destination or user location
        const firstDestination = formData.destinations && formData.destinations.length > 0 
          ? formData.destinations.filter(d => d.trim())[0] 
          : null;
        
        if (firstDestination && formData.location) {
          locationToUse = formData.location;
        } else if (firstDestination) {
          // Use first destination as address (will be geocoded)
          locationToUse = { lat: 0, lng: 0, address: firstDestination };
        } else {
          locationToUse = userLocation ? { ...userLocation, address: 'Your location' } : null;
        }
      } else {
        // For existing trips (view mode), ALWAYS use first destination name to geocode
        const firstDestination = trip?.destinations && trip.destinations.length > 0 
          ? trip.destinations[0] 
          : null;
        
        if (firstDestination) {
          // ALWAYS geocode destination name for proper regional view (don't use stored location)
          locationToUse = { lat: 0, lng: 0, address: firstDestination };
          console.log('🔍 Will geocode destination:', firstDestination);
        } else if (trip?.location && trip.location.lat !== 0 && trip.location.lng !== 0) {
          // Only use stored location if we have no destinations and coordinates are valid
          locationToUse = trip.location;
          console.log('📍 Using stored trip location:', trip.location);
        } else {
          locationToUse = null;
        }
      }

      // If still no location, use default (NYC) or user location as fallback
      if (!locationToUse) {
        if (userLocation) {
          locationToUse = { ...userLocation, address: 'Your location' };
        } else {
          // Default to NYC if nothing available
          locationToUse = { lat: 40.749933, lng: -73.98633, address: 'New York' };
        }
      }

      if (!mapRef.current) {
        console.log('⚠️ Map initialization skipped: mapRef.current is null in initializeMap');
        return;
      }

      // If locationToUse needs geocoding (lat/lng are 0), geocode it first
      if (locationToUse && locationToUse.lat === 0 && locationToUse.lng === 0 && locationToUse.address && 
          window.google?.maps?.Geocoder && !map) {
        const geocoder = new window.google.maps.Geocoder();
        const addressToGeocode = locationToUse.address;
        geocoder.geocode({ address: addressToGeocode }, (results: any, status: string) => {
          if (status === 'OK' && results && results[0]) {
            const loc = results[0].geometry.location;
            const geocodedLocation = { lat: loc.lat(), lng: loc.lng(), address: addressToGeocode };
            geocodedLocationRef.current = geocodedLocation;
            console.log('📍 Geocoded location for map center:', geocodedLocation);
            // Retry map initialization with geocoded location
            initializeMap();
          } else {
            console.warn('⚠️ Geocoding failed for:', addressToGeocode, status);
            // Use default location as fallback
            geocodedLocationRef.current = { lat: 40.749933, lng: -73.98633, address: 'New York' };
            initializeMap();
          }
        });
        return; // Exit and wait for geocoding callback
      }

      if (
        window.google && 
        window.google.maps && 
        typeof window.google.maps.Map === 'function' &&
        typeof window.google.maps.Marker === 'function'
      ) {
        try {
          // If map already exists, update markers instead of recreating the map
          if (map && mapRef.current) {
            // Clear existing markers
            markersRef.current.forEach((marker) => {
              marker.map = null;
            });
            markersRef.current = [];

            // Geocode all destinations and add markers to existing map
            const destinationsToShow = isEditMode || isNewTrip 
              ? formData.destinations.filter((d: string) => d.trim())
              : (trip?.destinations || []).filter((d: string) => d.trim());
            
            if (destinationsToShow.length > 0 && window.google?.maps?.Geocoder && window.google?.maps?.Marker) {
              const geocoder = new window.google.maps.Geocoder();
              const bounds = new window.google.maps.LatLngBounds();
              const newMarkers: any[] = [];
              let geocodeCount = 0;

              destinationsToShow.forEach((destination: string) => {
                geocoder.geocode({ address: destination }, (results: any, status: string) => {
                  geocodeCount++;
                  if (status === 'OK' && results && results[0]) {
                    const loc = results[0].geometry.location;
                    const position = { lat: loc.lat(), lng: loc.lng() };
                    
                    // Add marker to existing map
                    const marker = new window.google.maps.Marker({
                      map: map,
                      position: position,
                      title: destination
                    });
                    newMarkers.push(marker);
                    bounds.extend(position);

                    // If this is the last geocode, fit bounds and update markers ref
                    if (geocodeCount === destinationsToShow.length) {
                      markersRef.current = newMarkers;
                      // Only use fitBounds if there are multiple markers
                      if (newMarkers.length > 1) {
                        map.fitBounds(bounds);
                        const padding = 50;
                        map.fitBounds(bounds, { top: padding, right: padding, bottom: padding, left: padding });
                      } else if (newMarkers.length === 1) {
                        // For single destination, center on it with fixed zoom
                        map.setCenter(position);
                        map.setZoom(13);
                      }
                    }
                  } else if (geocodeCount === destinationsToShow.length) {
                    // Even if some failed, update markers with what we have
                    markersRef.current = newMarkers;
                    if (newMarkers.length > 1) {
                      map.fitBounds(bounds);
                      const padding = 50;
                      map.fitBounds(bounds, { top: padding, right: padding, bottom: padding, left: padding });
                    } else if (newMarkers.length === 1) {
                      // For single destination, use fixed zoom
                      map.setZoom(13);
                    }
                  }
                });
              });
            }
            return; // Don't recreate the map, just update markers
          }

          // Don't create map if we don't have valid coordinates
          if (!locationToUse || locationToUse.lat === 0 || locationToUse.lng === 0) {
            console.warn('⚠️ Map creation skipped: invalid coordinates', locationToUse);
            return;
          }

          console.log('✅ Creating map with coordinates:', { 
            lat: locationToUse.lat, 
            lng: locationToUse.lng, 
            address: locationToUse.address,
            zoom: 13
          });

          // Create new map instance (only when map doesn't exist yet)
          const mapInstance = new window.google.maps.Map(mapRef.current, {
            center: { lat: locationToUse.lat, lng: locationToUse.lng },
            zoom: 13,
            styles: [
              {
                featureType: "poi",
                elementType: "labels",
                stylers: [{ visibility: "on" }]
              }
            ]
          });

          // Clear existing markers from ref
          markersRef.current.forEach((marker) => {
            marker.map = null;
          });
          markersRef.current = [];

          // Geocode all destinations and add markers
          const destinationsToShow = isEditMode || isNewTrip 
            ? formData.destinations.filter((d: string) => d.trim())
            : (trip?.destinations || []).filter((d: string) => d.trim());
          
          if (destinationsToShow.length > 0 && window.google?.maps?.Geocoder && window.google?.maps?.Marker) {
            const geocoder = new window.google.maps.Geocoder();
            const bounds = new window.google.maps.LatLngBounds();
            const newMarkers: any[] = [];
            let geocodeCount = 0;

            destinationsToShow.forEach((destination: string) => {
              geocoder.geocode({ address: destination }, (results: any, status: string) => {
                geocodeCount++;
                if (status === 'OK' && results && results[0]) {
                  const loc = results[0].geometry.location;
                  const position = { lat: loc.lat(), lng: loc.lng() };
                  
                  // Add marker
                  const marker = new window.google.maps.Marker({
                    map: mapInstance,
                    position: position,
                    title: destination
                  });
                  newMarkers.push(marker);
                  bounds.extend(position);

                  // If this is the last geocode, fit bounds and update markers ref
                  if (geocodeCount === destinationsToShow.length) {
                    markersRef.current = newMarkers;
                    // Only use fitBounds if there are multiple markers
                    // For single destination, keep the initial zoom level
                    if (newMarkers.length > 1) {
                      mapInstance.fitBounds(bounds);
                      const padding = 50;
                      mapInstance.fitBounds(bounds, { top: padding, right: padding, bottom: padding, left: padding });
                      console.log('📍 Fit bounds for', newMarkers.length, 'markers');
                    } else if (newMarkers.length === 1) {
                      // For single destination, center on it with fixed zoom
                      mapInstance.setCenter(position);
                      mapInstance.setZoom(13);
                      console.log('📍 Centered on single marker with zoom 13');
                    }
                  }
                } else if (geocodeCount === destinationsToShow.length) {
                  // Even if some failed, update markers with what we have
                  markersRef.current = newMarkers;
                  if (newMarkers.length > 1) {
                    mapInstance.fitBounds(bounds);
                    const padding = 50;
                    mapInstance.fitBounds(bounds, { top: padding, right: padding, bottom: padding, left: padding });
                  } else if (newMarkers.length === 1) {
                    // For single destination, use fixed zoom
                    mapInstance.setZoom(13);
                  }
                }
              });
            });

            // If no destinations or geocoding fails, fall back to single location marker
            if (destinationsToShow.length === 0 && locationToUse && locationToUse.lat !== 0 && locationToUse.lng !== 0) {
              const fallbackMarker = new window.google.maps.Marker({
                map: mapInstance,
                position: { lat: locationToUse.lat, lng: locationToUse.lng },
                title: locationToUse.address || (isEditMode ? formData.title : trip?.title)
              });
              markersRef.current = [fallbackMarker];
            }
          } else if (locationToUse && locationToUse.lat !== 0 && locationToUse.lng !== 0) {
            // Fallback: single marker for location
            const singleMarker = new window.google.maps.Marker({
              map: mapInstance,
              position: { lat: locationToUse.lat, lng: locationToUse.lng },
              title: locationToUse.address || (isEditMode ? formData.destinations : trip?.destinations)?.join(', ') || (isEditMode ? formData.title : trip?.title)
            });
            markersRef.current = [singleMarker];
          }

          setMap(mapInstance);
          console.log('✅ Google Map initialized for trip', { location: locationToUse, destinations: destinationsToShow, tripId, isNewTrip, isEditMode });
        } catch (error) {
          console.error('❌ Error initializing Google Map:', error);
        }
      } else {
        console.log('⚠️ Google Maps API not ready yet', {
          hasGoogle: !!window.google,
          hasMaps: !!window.google?.maps,
          hasMapConstructor: typeof window.google?.maps?.Map === 'function',
          hasMarker: typeof window.google?.maps?.Marker === 'function'
        });
      }
    };

    // Check if Google Maps is already fully loaded
    // API is loaded globally, so just poll for it to be ready
    if (
      window.google && 
      window.google.maps && 
      typeof window.google.maps.Map === 'function' &&
      window.google.maps.marker &&
      typeof window.google.maps.marker.AdvancedMarkerElement === 'function'
    ) {
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(() => initializeMap(), 100);
      return () => clearTimeout(timeoutId);
    } else {
      // Poll for Google Maps API to be loaded globally
      checkInterval = setInterval(() => {
        if (
          window.google && 
          window.google.maps && 
          typeof window.google.maps.Map === 'function' &&
          window.google.maps.marker &&
          typeof window.google.maps.marker.AdvancedMarkerElement === 'function'
        ) {
          if (checkInterval) {
            clearInterval(checkInterval);
            checkInterval = null;
          }
          setTimeout(() => initializeMap(), 100);
        }
      }, 100);
      
      // Timeout after 15 seconds to prevent infinite polling
      const timeoutId = setTimeout(() => {
        if (checkInterval) {
          clearInterval(checkInterval);
          checkInterval = null;
          console.warn('⚠️ Google Maps API loading timeout. Please check your API key and network connection.');
        }
      }, 15000);

      // Cleanup interval on unmount or when dependencies change
      return () => {
        if (checkInterval) {
          clearInterval(checkInterval);
          checkInterval = null;
        }
        clearTimeout(timeoutId);
      };
    }
  }, [hasApiKey, trip, map, isEditMode, formData.location, formData.destinations, formData.title, isLoading, isNewTrip, tripId, userLocation]);

  if (isLoading) {
    return (
      <div className="trip-detail-content">
        <div className="trip-loading">
          <div className="loading-spinner">⏳</div>
          <p>Loading trip details...</p>
        </div>
      </div>
    );
  }

  if (error && !isNewTrip) {
    return (
      <div className="trip-detail-content">
        <div className="trip-not-found">
          <h2>{error ? 'Error loading trip' : 'Trip not found'}</h2>
          <p>{error || "The trip you're looking for doesn't exist."}</p>
        </div>
      </div>
    );
  }

  // For new trips or when in edit mode, show edit form
  const displayTrip = isNewTrip ? null : trip;
  const tripForDisplay = displayTrip || { 
    title: formData.title || 'New Trip',
    description: formData.description || '',
    location: formData.location || null,
    budget: formData.budget || '',
    budgetCurrency: formData.budgetCurrency || 'USD',
    destinations: formData.destinations,
    dates: formData.dates || { type: 'concrete' as const, startDate: '', endDate: '' },
    progress: 0,
    activities: [],
    rating: 0,
    memories: 0,
    status: 'draft' as const,
    images: ['✈️']
  };

  // If in edit mode or new trip, show edit form
  if (isEditMode || isNewTrip) {
    return (
      <div className="trip-detail-content">
        <div className="trip-detail-header">
          <div className="trip-title-section">
            <div className="trip-icon-large">✈️</div>
            <div style={{ width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '24px', color: '#64748b' }}>✏️</span>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Trip Title *"
                    className={`trip-title-input ${validationErrors.title ? 'error' : ''}`}
                    style={{
                      fontSize: '32px',
                      fontWeight: '700',
                      color: '#1e293b',
                      border: 'none',
                      padding: '8px 0',
                      flex: 1,
                      background: 'transparent',
                      borderBottom: validationErrors.title ? '2px solid #ef4444' : 'none'
                    }}
                    required
                  />
                  {validationErrors.title && (
                    <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                      {validationErrors.title}
                    </span>
                  )}
                </div>
              </div>
              <p className="trip-status-badge" style={{ margin: '8px 0 0 36px' }}>🚀 Draft</p>
            </div>
          </div>
        </div>

        {saveError && (
          <div style={{
            padding: '12px',
            background: '#fee2e2',
            border: '1px solid #fca5a5',
            borderRadius: '8px',
            color: '#dc2626',
            marginBottom: '24px'
          }}>
            ❌ {saveError}
          </div>
        )}

        <div className="trip-detail-grid">
          {/* Left Column - Trip Information */}
          <div className="trip-info-section">
            {/* Destinations */}
            <div className="info-card">
              <h3>📍 Destinations <span style={{ color: '#ef4444' }}>*</span></h3>
              <div className="info-content">
                {/* Display destinations as tags/chips */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  {formData.destinations.map((dest, index) => (
                    <div
                      key={index}
                      className="destination-tag"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        background: '#f0f9ff',
                        border: '1px solid #3b82f6',
                        borderRadius: '20px',
                        fontSize: '14px',
                        color: '#1e293b'
                      }}
                    >
                      <span>{dest}</span>
                      <button
                        onClick={() => handleRemoveDestination(index)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#64748b',
                          cursor: 'pointer',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: '16px',
                          lineHeight: '1',
                          marginLeft: '2px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#dc2626';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#64748b';
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                {/* Input for adding new destination */}
                <DestinationInput
                  onAdd={async (destination: string) => {
                    const newDestinations = [...formData.destinations, destination];
                    handleInputChange('destinations', newDestinations);
                    
                    // Geocode the destination and update location (use first destination)
                    if (newDestinations.length === 1 && window.google?.maps?.Geocoder) {
                      const geocoder = new window.google.maps.Geocoder();
                      geocoder.geocode({ address: destination }, (results: any, status: string) => {
                        if (status === 'OK' && results && results[0]) {
                          const loc = results[0].geometry.location;
                          handleInputChange('location', {
                            lat: loc.lat(),
                            lng: loc.lng(),
                            address: destination
                          });
                          // Clear map to trigger re-initialization with new location
                          setMap(null);
                        }
                      });
                    }
                  }}
                />
              </div>
            </div>

            {/* Trip Dates - Required */}
            <div className="info-card">
              <h3>📅 Trip Dates <span style={{ color: '#ef4444' }}>*</span></h3>
              <div className="info-content">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <label style={{ fontSize: '14px', fontWeight: '600' }}>Start Date</label>
                      <button
                        type="button"
                        onClick={() => {
                          const today = new Date().toISOString().split('T')[0];
                          handleInputChange('dates', {
                            ...formData.dates,
                            type: 'concrete',
                            startDate: today,
                            endDate: formData.dates?.endDate || today
                          } as TripDates);
                        }}
                        style={{
                          padding: '4px 12px',
                          fontSize: '12px',
                          background: '#f1f5f9',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          color: '#475569',
                          fontWeight: '500'
                        }}
                      >
                        Today
                      </button>
                    </div>
                    <input
                      type="date"
                      value={formData.dates?.startDate || ''}
                      onChange={(e) => handleInputChange('dates', {
                        ...formData.dates,
                        type: 'concrete',
                        startDate: e.target.value,
                        endDate: formData.dates?.endDate || ''
                      } as TripDates)}
                      className={`setting-input ${validationErrors.dates ? 'error' : ''}`}
                      required
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <label style={{ fontSize: '14px', fontWeight: '600' }}>End Date</label>
                      <button
                        type="button"
                        onClick={() => {
                          const today = new Date().toISOString().split('T')[0];
                          handleInputChange('dates', {
                            ...formData.dates,
                            type: 'concrete',
                            startDate: formData.dates?.startDate || today,
                            endDate: today
                          } as TripDates);
                        }}
                        style={{
                          padding: '4px 12px',
                          fontSize: '12px',
                          background: '#f1f5f9',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          color: '#475569',
                          fontWeight: '500'
                        }}
                      >
                        Today
                      </button>
                    </div>
                    <input
                      type="date"
                      value={formData.dates?.endDate || ''}
                      onChange={(e) => handleInputChange('dates', {
                        ...formData.dates,
                        type: 'concrete',
                        startDate: formData.dates?.startDate || '',
                        endDate: e.target.value
                      } as TripDates)}
                      className={`setting-input ${validationErrors.dates ? 'error' : ''}`}
                      required
                      style={{ width: '100%' }}
                      min={formData.dates?.startDate || ''}
                    />
                  </div>
                </div>
                {validationErrors.dates && (
                  <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {validationErrors.dates}
                  </span>
                )}
              </div>
            </div>

            {/* Budget */}
            <div className="info-card">
              <h3>💰 Budget</h3>
              <div className="info-content">
                <input
                  type="text"
                  value={formData.budget}
                  onChange={(e) => {
                    // Only allow numbers and one dot
                    let value = e.target.value;
                    // Remove all characters except digits and dots
                    value = value.replace(/[^\d.]/g, '');
                    // Ensure only one dot
                    const dotIndex = value.indexOf('.');
                    if (dotIndex !== -1) {
                      value = value.substring(0, dotIndex + 1) + value.substring(dotIndex + 1).replace(/\./g, '');
                    }
                    handleInputChange('budget', value);
                  }}
                  placeholder="Enter budget amount"
                  className={`setting-input ${validationErrors.budget ? 'error' : ''}`}
                  style={{ width: '100%' }}
                />
                {validationErrors.budget && (
                  <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {validationErrors.budget}
                  </span>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="info-card">
              <h3>📝 Description</h3>
              <div className="info-content">
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter trip description"
                  className="setting-input"
                  style={{ width: '100%', minHeight: '120px', resize: 'vertical' }}
                />
              </div>
            </div>
          </div>

          {/* Right Column - Map & Location */}
          <div className="trip-map-section">
                  {hasApiKey ? (
              <div className="gmp-place-picker-container">
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <div 
                    ref={mapRef}
                    className="google-map-container"
                    style={{
                      height: '400px',
                      width: '100%',
                      borderRadius: '12px',
                      border: '2px solid #e2e8f0',
                      backgroundColor: '#f8fafc'
                    }}
                  />
                </div>
              </div>
                  ) : (
                    <div className="map-placeholder">
                      <p>Google Maps API key is not configured</p>
                <p>Please set REACT_APP_GOOGLE_MAPS_API_KEY in your .env file</p>
                    </div>
                  )}
          </div>
        </div>

        {/* Save, Cancel, and Delete Buttons */}
        <div className="trip-actions-section" style={{ display: 'flex', gap: '12px' }}>
          {/* Delete button (only show for existing trips) */}
          {!isNewTrip && (
            <button 
              className="btn-secondary" 
              onClick={handleDelete}
              disabled={isSaving}
              style={{ 
                padding: '16px', 
                fontSize: '16px', 
                fontWeight: '600',
                background: '#fee2e2',
                color: '#dc2626',
                border: '1px solid #fecaca'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#fecaca';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fee2e2';
              }}
            >
              🗑️ Delete Trip
            </button>
          )}
          <button 
            className="btn-secondary" 
            onClick={handleCancel}
            disabled={isSaving}
            style={{ flex: 1, padding: '16px', fontSize: '16px', fontWeight: '600' }}
          >
            Cancel
          </button>
          <button 
            className="btn-primary" 
            onClick={handleSave}
            disabled={isSaving}
            style={{ flex: 1, padding: '16px', fontSize: '16px', fontWeight: '600' }}
          >
            {isSaving ? 'Saving...' : 'Save Trip'}
          </button>
        </div>
      </div>
    );
  }

  // Format dates for display
  const formattedDates = formatTripDates(tripForDisplay.dates);
  
  // Get first image or default emoji
  const tripImage = tripForDisplay.images && tripForDisplay.images.length > 0 ? tripForDisplay.images[0] : '✈️';

  return (
    <div className="trip-detail-content">
      <div className="trip-detail-header">
        <div className="trip-title-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div className="trip-icon-large">{tripImage}</div>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <h1 style={{ margin: 0, lineHeight: 1.2 }}>{tripForDisplay.title}</h1>
              <p className="trip-status-badge" style={{ margin: '4px 0 0 0' }}>{getStatusDisplay(tripForDisplay.status)}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button 
              className="btn-icon" 
              onClick={handleEdit}
              title="Edit Trip"
              style={{
                padding: '10px 20px',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#2563eb'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#3b82f6'}
            >
              ✏️ Edit
            </button>
            <button 
              className="btn-icon"
              title="Share Trip"
              style={{
                padding: '10px 20px',
                background: '#f1f5f9',
                color: '#475569',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#e2e8f0';
                e.currentTarget.style.borderColor = '#94a3b8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#f1f5f9';
                e.currentTarget.style.borderColor = '#cbd5e1';
              }}
            >
              🔗 Share
            </button>
            {normalizeStatus(tripForDisplay.status) === 'in_progress' && (
              <button 
                className="btn-icon"
                title="Add Memories"
                style={{
                  padding: '10px 20px',
                  background: '#f1f5f9',
                  color: '#475569',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e2e8f0';
                  e.currentTarget.style.borderColor = '#94a3b8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f1f5f9';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                }}
              >
                📸 Memories
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="trip-detail-grid">
        {/* Left Column - Trip Information */}
        <div className="trip-info-section">
          {/* Combined Trip Details Block */}
            <div className="info-card">
            <h3 style={{ fontSize: '16px', margin: '0 0 12px 0' }}>📋 Trip Details</h3>
            <div className="info-content" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* Destinations */}
              {tripForDisplay.destinations && tripForDisplay.destinations.length > 0 && (
                <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                  <span style={{ fontWeight: '600', color: '#64748b' }}>📍 Destinations</span>
                  <div style={{ marginTop: '2px', color: '#333' }}>
                  {formatDestinations(tripForDisplay.destinations)}
              </div>
            </div>
          )}

              {/* Trip Dates */}
              <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                <span style={{ fontWeight: '600', color: '#64748b' }}>📅 Trip Dates</span>
                <div style={{ marginTop: '2px', color: '#333' }}>
                  <span style={{ marginRight: '8px' }}>Start Date</span>
                  <span style={{ marginRight: '8px' }}>{formatDateDisplay(tripForDisplay.dates.startDate)}</span>
                  <span style={{ margin: '0 8px' }}>→</span>
                  <span style={{ marginRight: '8px' }}>End Date</span>
                  <span>{formatDateDisplay(tripForDisplay.dates.endDate)}</span>
                </div>
                </div>

              {/* Budget */}
          {tripForDisplay.budget && (
                <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                  <span style={{ fontWeight: '600', color: '#64748b' }}>💰 Budget</span>
                  <span style={{ marginLeft: '8px', color: '#10b981', fontWeight: '600' }}>
                  {formatBudget(tripForDisplay.budget)}
                </span>
              </div>
          )}

              {/* Description */}
          {tripForDisplay.description && (
                <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                  <span style={{ fontWeight: '600', color: '#64748b' }}>📝 Description</span>
                  <div style={{ marginTop: '2px', color: '#333' }}>
                    {tripForDisplay.description}
              </div>
            </div>
          )}
            </div>
          </div>

          {/* Trip Chat Assistant */}
          <div className="info-card" style={{ padding: 0, border: 'none', background: 'transparent', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div className="websocket-chat-container" style={{ flex: 1, border: 'none', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div className="chat-messages" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                  {isLoadingConversation ? (
                    <div className="chat-welcome">
                      <p>Loading conversation...</p>
                    </div>
                  ) : chatMessages.length === 0 ? (
                    <div className="chat-welcome">
                      <p>👋 Hi! I'm your trip assistant. Ask me anything about your trip to {tripForDisplay.destinations?.join(', ')} or get recommendations!</p>
                    </div>
                  ) : (
                    <>
                      {chatMessages.map((msg) => (
                        <div key={msg.id} className={`chat-message ${msg.isUser ? 'user-message' : 'assistant-message'}`}>
                          <div className="message-content">
                            {msg.message}
                          </div>
                          
                          {/* Show trip draft if present (disabled for historical messages) */}
                          {msg.intent === 'CONFIRM_TRIP' && msg.trip_draft && msg.isHistorical && (
                            <div style={{
                              marginTop: '16px',
                              padding: '20px',
                              background: '#1e293b',
                              border: '2px solid #334155',
                              borderRadius: '12px',
                              opacity: 0.85
                            }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '16px'
                              }}>
                                <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#f1f5f9' }}>
                                  📋 Trip Details
                                </h4>
                                <span style={{
                                  fontSize: '12px',
                                  color: '#94a3b8',
                                  fontStyle: 'italic',
                                  background: '#0f172a',
                                  padding: '4px 12px',
                                  borderRadius: '12px',
                                  border: '1px solid #334155'
                                }}>
                                  Already confirmed
                </span>
                              </div>

                              {/* Title */}
                              {msg.trip_draft.title && (
                                <div style={{ marginBottom: '16px' }}>
                                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#94a3b8' }}>
                                    Trip Title
                                  </label>
                                  <div style={{
                                    padding: '10px 12px',
                                    background: '#0f172a',
                                    border: '1px solid #475569',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    color: '#cbd5e1'
                                  }}>
                                    {msg.trip_draft.title}
              </div>
            </div>
          )}

                              {/* Destinations */}
                              <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#94a3b8' }}>
                                  Destinations
                                </label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                  {(msg.trip_draft.destinations || []).map((dest: string, idx: number) => (
                                    <div key={idx} style={{
                                      padding: '6px 12px',
                                      background: '#0f172a',
                                      border: '1px solid #475569',
                                      borderRadius: '16px',
                                      fontSize: '13px',
                                      color: '#f1f5f9'
                                    }}>
                                      📍 {dest}
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Dates */}
                              {msg.trip_draft.dates && (
                                <div style={{ marginBottom: '16px' }}>
                                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#94a3b8' }}>
                                    Trip Dates
                                  </label>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '8px', alignItems: 'center' }}>
                                    <div>
                                      <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>Start Date</div>
                                      <div style={{
                                        padding: '8px 12px',
                                        background: '#0f172a',
                                        border: '1px solid #475569',
                                        borderRadius: '8px',
                                        fontSize: '13px',
                                        color: '#cbd5e1'
                                      }}>
                                        {msg.trip_draft.dates.startDate}
                                      </div>
                                    </div>
                                    <div style={{ color: '#64748b', fontSize: '16px', paddingTop: '16px' }}>→</div>
                                    <div>
                                      <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>End Date</div>
                                      <div style={{
                                        padding: '8px 12px',
                                        background: '#0f172a',
                                        border: '1px solid #475569',
                                        borderRadius: '8px',
                                        fontSize: '13px',
                                        color: '#cbd5e1'
                                      }}>
                                        {msg.trip_draft.dates.endDate}
                                      </div>
                                    </div>
              </div>
            </div>
          )}

                              {/* Budget */}
                              {msg.trip_draft.budget && (
                                <div style={{ marginBottom: '16px' }}>
                                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#94a3b8' }}>
                                    Budget
                                  </label>
                                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '8px' }}>
                                    <div>
                                      <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>Amount</div>
                                      <div style={{
                                        padding: '8px 12px',
                                        background: '#0f172a',
                                        border: '1px solid #475569',
                                        borderRadius: '8px',
                                        fontSize: '13px',
                                        color: '#cbd5e1'
                                      }}>
                                        {msg.trip_draft.budget.amount}
                                      </div>
                                    </div>
                                    <div>
                                      <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>Currency</div>
                                      <div style={{
                                        padding: '8px 12px',
                                        background: '#0f172a',
                                        border: '1px solid #475569',
                                        borderRadius: '8px',
                                        fontSize: '13px',
                                        color: '#cbd5e1'
                                      }}>
                                        {msg.trip_draft.budget.currency}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Description */}
                              {msg.trip_draft.description && (
                                <div>
                                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#94a3b8' }}>
                                    Description <span style={{ color: '#64748b', fontWeight: '400' }}>(optional)</span>
                                  </label>
                                  <div style={{
                                    padding: '10px 12px',
                                    background: '#0f172a',
                                    border: '1px solid #475569',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    color: '#cbd5e1',
                                    lineHeight: '1.5'
                                  }}>
                                    {msg.trip_draft.description}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                      <div ref={chatMessagesEndRef} />
                    </>
                  )}
                </div>
                <div className="chat-input-wrapper">
                  <input
                    type="text"
                    className="chat-input"
                    placeholder="Ask about your trip..."
                  />
                  <button className="chat-send-button">
                    Send
                  </button>
                </div>
            </div>
          </div>

          {tripForDisplay.progress !== undefined && tripForDisplay.progress > 0 && (
            <div className="info-card">
              <h3>📊 Progress</h3>
              <div className="info-content">
                <ProgressBar progress={tripForDisplay.progress} />
              </div>
            </div>
          )}

          {(() => {
            const daysRemaining = calculateDaysRemaining(tripForDisplay.dates);
            return daysRemaining !== null && daysRemaining > 0 && (
              <div className="info-card">
                <h3>⏰ Time Remaining</h3>
                <div className="info-content">
                  <DaysRemaining dates={tripForDisplay.dates} />
                </div>
              </div>
            );
          })()}

          {tripForDisplay.activities && tripForDisplay.activities.length > 0 && (
            <div className="info-card">
              <h3>🎯 Activities</h3>
              <div className="info-content">
                <ul className="activities-list">
                  {tripForDisplay.activities.map((activity: string, index: number) => (
                    <li key={index}>{activity}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {tripForDisplay.rating && (
            <div className="info-card">
              <h3>⭐ Rating</h3>
              <div className="info-content">
                <span className="rating-stars">{'⭐'.repeat(tripForDisplay.rating)}</span>
              </div>
            </div>
          )}

          {tripForDisplay.memories && (
            <div className="info-card">
              <h3>📸 Memories</h3>
              <div className="info-content">
                <span className="memories-count">{tripForDisplay.memories} photos</span>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Map */}
        <div className="trip-map-section">
              {hasApiKey ? (
            <div className="gmp-place-picker-container">
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div 
                  ref={mapRef}
                  className="google-map-container"
                  style={{
                    height: '400px',
                    width: '100%',
                    borderRadius: '12px',
                    border: '2px solid #e2e8f0',
                    backgroundColor: '#f8fafc'
                  }}
                />
              </div>
            </div>
              ) : (
                <div className="map-placeholder">
                  <p>Google Maps API key is not configured</p>
                  <p>Please set REACT_APP_GOOGLE_MAPS_API_KEY in your .env file</p>
                </div>
              )}

          {/* Activities Section */}
          <div className="info-card" style={{ marginTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>🎯 Activities</h3>
              <button
                onClick={handleCreateActivity}
                style={{
                  padding: '8px 16px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#2563eb'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#3b82f6'}
              >
                + Create Activity
              </button>
            </div>

            {isLoadingActivities ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                Loading activities...
              </div>
            ) : activities.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#64748b', fontSize: '14px' }}>
                No activities yet. Create your first activity!
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {activities.map((activity) => (
                  <div 
                    key={activity.id}
                    style={{
                      padding: '12px 16px',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'start'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px', fontSize: '14px' }}>
                        {activity.title}
                      </div>
                      <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '2px' }}>
                        📍 {activity.location}
                      </div>
                      {activity.dateTime && (
                        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '2px' }}>
                          📅 {new Date(activity.dateTime).toLocaleString()}
                </div>
              )}
                      {activity.budget && (
                        <div style={{ fontSize: '13px', color: '#10b981', fontWeight: '600' }}>
                          💰 {activity.budget.currency} {activity.budget.amount}
            </div>
                      )}
                      <div style={{ 
                        display: 'inline-block',
                        marginTop: '6px',
                        padding: '2px 8px',
                        background: activity.status === 'scheduled' ? '#dbeafe' : '#f1f5f9',
                        color: activity.status === 'scheduled' ? '#1e40af' : '#475569',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {activity.status === 'scheduled' ? '📅 Scheduled' : '📝 Planned'}
          </div>
        </div>
                    <button
                      onClick={() => handleEditActivity(activity)}
                      style={{
                        padding: '6px 12px',
                        background: 'white',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#64748b',
                        cursor: 'pointer',
                        marginLeft: '12px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f1f5f9';
                        e.currentTarget.style.borderColor = '#94a3b8';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.borderColor = '#cbd5e1';
                      }}
                    >
                      ✏️ Edit
                    </button>
                  </div>
                ))}
              </div>
            )}
      </div>

          {/* Activity Form Modal */}
          {showActivityForm && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px'
            }}
            onClick={handleCancelActivityForm}
            >
              <div 
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '24px',
                  maxWidth: '500px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflowY: 'auto'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>
                  {editingActivity ? 'Edit Activity' : 'Create New Activity'}
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#475569' }}>
                      Title *
                    </label>
                    <input
                      type="text"
                      value={activityFormData.title}
                      onChange={(e) => setActivityFormData({ ...activityFormData, title: e.target.value })}
                      placeholder="e.g., Visit CN Tower"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        fontSize: '14px',
                        outline: 'none'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#475569' }}>
                      Location *
                    </label>
                    <input
                      type="text"
                      value={activityFormData.location}
                      onChange={(e) => setActivityFormData({ ...activityFormData, location: e.target.value })}
                      placeholder="e.g., CN Tower, Toronto"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        fontSize: '14px',
                        outline: 'none'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#475569' }}>
                      Date & Time (Optional)
                    </label>
                    <input
                      type="datetime-local"
                      value={activityFormData.dateTime}
                      onChange={(e) => setActivityFormData({ ...activityFormData, dateTime: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        fontSize: '14px',
                        outline: 'none'
                      }}
                    />
                    <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                      Status will be "Scheduled" if date is provided, otherwise "Planned"
                    </p>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#475569' }}>
                      Budget (Optional)
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <select
                        value={activityFormData.budgetCurrency}
                        onChange={(e) => setActivityFormData({ ...activityFormData, budgetCurrency: e.target.value })}
                        style={{
                          padding: '10px 12px',
                          border: '1px solid #cbd5e1',
                          borderRadius: '8px',
                          fontSize: '14px',
                          outline: 'none',
                          background: 'white'
                        }}
                      >
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                        <option value="CAD">CAD</option>
                      </select>
                      <input
                        type="number"
                        value={activityFormData.budget}
                        onChange={(e) => setActivityFormData({ ...activityFormData, budget: e.target.value })}
                        placeholder="Amount"
                        min="0"
                        step="0.01"
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          border: '1px solid #cbd5e1',
                          borderRadius: '8px',
                          fontSize: '14px',
                          outline: 'none'
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                  <button
                    onClick={handleCancelActivityForm}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#f1f5f9',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#475569',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveActivity}
                    disabled={!activityFormData.title || !activityFormData.location}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: activityFormData.title && activityFormData.location ? '#3b82f6' : '#cbd5e1',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'white',
                      cursor: activityFormData.title && activityFormData.location ? 'pointer' : 'not-allowed'
                    }}
                  >
                    {editingActivity ? 'Update Activity' : 'Create Activity'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TripDetail;
